import { useState, useRef, useEffect, useCallback } from "react";
import { db, auth, googleProvider } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { signInWithPopup, onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";

export function useFirebase() {
  const [user, setUser] = useState(null);        // { email, name, picture }
  const [status, setStatus] = useState("checking");   // checking | idle | signing-in | loading | saving | error | ready
  const [error, setError] = useState(null);
  const [loadedData, setLoadedData] = useState(null);
  const unsubRef = useRef(null);

  // Listen to Firebase auth state — fires on both fresh sign-in AND session restore.
  // Load Firestore data here so data is always fetched regardless of how auth was established.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const email = (firebaseUser.email || "").toLowerCase();
        setUser({ email, name: firebaseUser.displayName || email.split("@")[0], picture: firebaseUser.photoURL || null });
        setStatus("loading");
        try {
          const snap = await getDoc(doc(db, "payroll", "data"));
          // Always call setLoadedData (even with empty object) so firestoreReadyRef unlocks saves
          setLoadedData(snap.exists() ? snap.data() : {});
        } catch (err) {
          console.error("Failed to load Firestore data:", err);
        }
        setStatus("ready");
      } else {
        setUser(null);
        setStatus("idle");
      }
    });
    // Safety timeout: if auth check takes too long, show login
    const timeout = setTimeout(() => setStatus((s) => s === "checking" ? "idle" : s), 3000);
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  const signIn = useCallback(async () => {
    try {
      setStatus("signing-in");
      setError(null);
      googleProvider.setCustomParameters({ hd: "easy-skill.com" });
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged above handles loading data
    } catch (err) {
      setStatus("error");
      setError(err.message || "Sign-in failed");
    }
  }, []);

  const save = useCallback(async (data) => {
    if (!auth.currentUser) return;
    try {
      setStatus("saving");
      await setDoc(doc(db, "payroll", "data"), data);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
      setError("Save failed: " + (err.message || "unknown"));
    }
  }, []);

  const doSignOut = useCallback(async () => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    await firebaseSignOut(auth);
    setUser(null); setStatus("idle");
  }, []);

  return { user, status, error, loadedData, signIn, save, signOut: doSignOut, isSignedIn: !!user };
}
